// =============================================================================
// MeuDriver — Landing Stats Bar + How It Works (Sections 2 + 3)
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { Search, Calendar, Car } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { PLATFORM_STATS } from '@/lib/constants'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay },
  }),
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

const viewportOnce = { once: true, margin: '-80px' }

function AnimatedCounter({ to, suffix = '' }: { to: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true })
  const [displayed, setDisplayed] = useState(0)

  useEffect(() => {
    if (!isInView) return
    const duration = 1400
    const start = performance.now()
    const step = (ts: number) => {
      const progress = Math.min((ts - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.floor(eased * to))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [isInView, to])

  return (
    <span ref={ref}>
      {displayed.toLocaleString('pt-BR')}
      {suffix}
    </span>
  )
}

const STATS = [
  { num: PLATFORM_STATS.instructors, suffix: '+', label: 'Instrutores', color: 'text-brand-600' },
  { num: PLATFORM_STATS.lessonsDelivered, suffix: '+', label: 'Aulas realizadas', color: 'text-teal-600' },
  { num: PLATFORM_STATS.averageRating, suffix: '', label: 'Avaliação média', color: 'text-orange-500', isDecimal: true },
  { num: PLATFORM_STATS.cities, suffix: '+', label: 'Cidades', color: 'text-brand-600' },
]

const HOW_IT_WORKS = [
  {
    number: '01',
    icon: <Search className="h-7 w-7" />,
    title: 'Encontre um instrutor',
    description: 'Busque por cidade, especialidade e disponibilidade. Compare avaliações reais.',
    gradient: 'from-brand-500 to-brand-700',
    bg: 'from-brand-50 to-brand-100/60',
    border: 'border-brand-100',
  },
  {
    number: '02',
    icon: <Calendar className="h-7 w-7" />,
    title: 'Agende sua aula',
    description: 'Escolha data, horário e local de partida. Confirme diretamente pela plataforma.',
    gradient: 'from-teal-500 to-teal-700',
    bg: 'from-teal-50 to-teal-100/60',
    border: 'border-teal-100',
  },
  {
    number: '03',
    icon: <Car className="h-7 w-7" />,
    title: 'Aprenda a dirigir',
    description: 'Realize a aula com instrutor qualificado. Pague com segurança e avalie ao final.',
    gradient: 'from-orange-500 to-orange-600',
    bg: 'from-orange-50 to-orange-100/60',
    border: 'border-orange-100',
  },
]

export function LandingStats() {
  return (
    <section className="border-y border-gray-100 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce} className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          {STATS.map((stat) => (
            <motion.div key={stat.label} variants={fadeUp} className="flex flex-col items-center gap-1 text-center">
              <span className={`text-3xl font-extrabold sm:text-4xl ${stat.color}`}>
                {stat.isDecimal ? <span>{stat.num}</span> : <AnimatedCounter to={stat.num} suffix={stat.suffix} />}
              </span>
              <span className="text-sm text-gray-500 font-medium">{stat.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export function LandingHowItWorks() {
  return (
    <section className="bg-slate-50/70 py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5 }} className="text-center mb-14">
          <Badge variant="teal" className="mb-3 text-sm px-3 py-1">Simples e rápido</Badge>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl tracking-tight">Como funciona</h2>
          <p className="mt-3 text-gray-500 max-w-lg mx-auto">Três passos simples para começar sua jornada ao volante.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce} className="grid gap-5 sm:grid-cols-3 relative">
          <div className="hidden sm:block absolute top-12 left-[calc(33.33%-1px)] w-1/3 h-px bg-gradient-to-r from-brand-200 to-teal-200 pointer-events-none" />
          <div className="hidden sm:block absolute top-12 left-[calc(66.66%-1px)] w-1/3 h-px bg-gradient-to-r from-teal-200 to-orange-200 pointer-events-none" />

          {HOW_IT_WORKS.map((step, i) => (
            <motion.div key={step.number} variants={fadeUp} custom={i * 0.08}>
              <Card className={`relative h-full overflow-hidden border bg-gradient-to-br ${step.bg} ${step.border} shadow-soft hover:shadow-card transition-all duration-300 group`}>
                <CardContent className="p-7">
                  <span className="absolute top-4 right-5 text-7xl font-black text-black/[0.04] select-none leading-none pointer-events-none">
                    {step.number}
                  </span>
                  <div className={`inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-gradient-to-br ${step.gradient} text-white shadow-md mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    {step.icon}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
